import { vec2 } from "@tlaukkan/tsm";
import { Debug } from "../../../shared/debug";
import { Sound } from "../../../shared/sound";
import { assertClient, worldToScreenPosition } from "../../../shared/utils";
import { GameClient } from "../gameClient";
import { CameraSystem } from "../systems/cameraSystem";
import { SoundEffect } from "./soundEffect";
import { SpatializedSoundEffect } from "./SpatializedSoundEffect";
import { DefaultSoundEffectVolumes } from "./volumes";
import { getMainVolume, getSfxVolume, getMusicVolume, getDontShowTutorial, getFrequentVolume } from "../userSettings";
import { CameraZoomLevel } from "../../../shared/constants";
import { loadZipAssetPack } from "../rendering/assetLoader";

assertClient();

const CROSSFADE_DURATION = 5; // Duration in seconds

//const CROSSFADE_LENGTH_HZ = 44100 * CROSSFADE_DURATION; // Assuming a sample rate of 44100Hz
// ^ Is huge! and causes perf issues. Instead, we'll use an abreviated sample rate
const CROSSFADE_LENGTH_HZ = 128 * CROSSFADE_DURATION; // Assuming a sample rate of 44100Hz

export class SoundEngine {
	gameClient: GameClient;
	musicLoading: Promise<void>;
	audioContext: AudioContext;
	compressor: DynamicsCompressorNode;
	musicGain: GainNode;
	sfxGain: GainNode;
	frequentUISoundsGain: GainNode;

	private cameraSytem!: CameraSystem;
	private sounds = new Array<SoundEffect | undefined>(Sound.END);
	// private music = new Map<Sound, SoundEffect>();
	// private uiSounds = new Map<Sound, SoundEffect>();

	masterGain: GainNode;
	private _lastVolume: number;
	private _isMuted: boolean;

	private musicReady!: () => void;

	private fadeCurveIn = new Float32Array(CROSSFADE_LENGTH_HZ);
	private fadeCurveOut = new Float32Array(CROSSFADE_LENGTH_HZ);
	private currentlyPlayingSimulation = false;
	private crossfadeEndTime = 0;
	private musicSources: AudioBufferSourceNode[] = [];
	private musicStarted = false;

	constructor(gameClient: GameClient) {
		this.gameClient = gameClient;
		this.musicLoading = new Promise((resolve, reject) => {
			this.musicReady = () => {
				resolve();
			};
		});

		this._lastVolume = .5;
		this._isMuted = false;

		this.audioContext = new AudioContext();

		this.masterGain = this.audioContext.createGain();
		this.masterGain.gain.value = 1;
		this.masterGain.connect(this.audioContext.destination);

		this.compressor = this.audioContext.createDynamicsCompressor();
		this.compressor.threshold.setValueAtTime(-0.1, this.audioContext.currentTime);
		this.compressor.knee.setValueAtTime(0, this.audioContext.currentTime);
		this.compressor.ratio.setValueAtTime(20, this.audioContext.currentTime);

		this.compressor.connect(this.masterGain);

		this.sfxGain = this.audioContext.createGain();
		this.sfxGain.gain.value = .2;
		this.sfxGain.connect(this.compressor);

		this.frequentUISoundsGain = this.audioContext.createGain();
		this.frequentUISoundsGain.gain.value = .2;
		this.frequentUISoundsGain.connect(this.sfxGain);

		this.musicGain = this.audioContext.createGain();
		// this.musicGain.gain.value = Debug.CaptureMode ? 0.05 : .2;
		this.musicGain.gain.value = Debug.CaptureMode ? 0.0 : 1;
		this.musicGain.connect(this.compressor);

		//Prepare enumarted curves for equal power crossfading for the music

		for (let i = 0; i < CROSSFADE_LENGTH_HZ; ++i) {
			let percent = i / (CROSSFADE_LENGTH_HZ - 1);
			this.fadeCurveIn[i] = Math.cos((1.0 - percent) * 0.5 * Math.PI);
			this.fadeCurveOut[i] = Math.cos(percent * 0.5 * Math.PI);
		}

		if (this.checkIfChrome()) {
			const audioListener = this.audioContext.listener;
			audioListener.forwardX.setValueAtTime(0, this.audioContext.currentTime);
			audioListener.forwardY.setValueAtTime(0, this.audioContext.currentTime);
			audioListener.forwardZ.setValueAtTime(-1, this.audioContext.currentTime);
			audioListener.upX.setValueAtTime(0, this.audioContext.currentTime);
			audioListener.upY.setValueAtTime(1, this.audioContext.currentTime);
			audioListener.upZ.setValueAtTime(0, this.audioContext.currentTime);
			audioListener.positionX.setValueAtTime(0, this.audioContext.currentTime);
			audioListener.positionY.setValueAtTime(0, this.audioContext.currentTime);
			audioListener.positionZ.setValueAtTime(1, this.audioContext.currentTime);
		}
	}

	destroy() {
		this.audioContext.close();
	}

	audioPack: Map<string, Uint8Array> = new Map<string, Uint8Array>();
	async init() {
		return new Promise<void>(async (resolve, reject) => {
			const t1 = performance.now();
			//Load sound assets from zip
			this.audioPack = await loadZipAssetPack("assets/sounds.zip");
			console.log(`SoundEngine init took ${performance.now() - t1} ms`);

			this.loadMusic();
			this.loadSoundEffects();
			this.loadUIEffects();

			// Music is started and stopped by the game states, but the volume settings still
			// need applying so menu UI sounds are at the user's chosen levels.
			this.musicLoading.then(() => {
				this.applyUserVolumes();
				console.log("SoundEngine: loaded music assets " + performance.now());
			});

			resolve();
		});
	}

	addSound(key: Sound, filenames: string[], options?: { frequent?: boolean }) {
		const effect = new SpatializedSoundEffect(
			this,
			this.cameraSytem,
			filenames,
			options?.frequent ? this.frequentUISoundsGain : this.sfxGain
		);
		effect.init();
		const volume = DefaultSoundEffectVolumes.get(key);
		if (volume) {
			effect.volume = volume;
		}
		else {
			console.warn(`No default volume for sound ${key}`);
		}
		this.sounds[key] = effect;
	}

	async addMusic(key: Sound, file: string) {
		const sound = new SoundEffect(this, [file], this.musicGain);
		await sound.init();
		const volume = DefaultSoundEffectVolumes.get(key);
		if (volume !== undefined && volume !== null) {
			sound.volume = volume;
		}
		else {
			console.warn(`No default volume for sound ${key}`);
		}
		this.sounds[key] = sound;
	}

	async addUISound(key: Sound, files: string[], options?: { frequent?: boolean }) {
		const node = options?.frequent ? this.frequentUISoundsGain : this.sfxGain;
		const sound = new SoundEffect(this, files, node);
		await sound.init();
		const volume = DefaultSoundEffectVolumes.get(key);
		if (volume) {
			sound.volume = volume;
		}
		else {
			console.warn(`No default volume for sound ${key}`);
		}
		this.sounds[key] = sound;
	}

	getSoundEffect(key: Sound) {
		return this.sounds[key];
	}

	playUISound(key: Sound) {
		const sound = this.sounds[key];
		if (sound) {
			sound.play();
		}
	}

	playSound(key: Sound, pos: vec2, zoomLevel: CameraZoomLevel = CameraZoomLevel.Normal, randomization = true) {
		if (key >= Sound.UI_SOUND_START) return;
		const sound = this.sounds[key] as SpatializedSoundEffect;

		if (sound) {
			const sceneRoot = this.gameClient.renderEngine.sceneRoot;
			const screenPos = worldToScreenPosition(pos);
			screenPos.x += (sceneRoot.position.x * sceneRoot.scale.x);
			screenPos.y -= (sceneRoot.position.y * sceneRoot.scale.y);
			sound.playDiegetic(screenPos, zoomLevel, randomization);
		}
	}

	private applyUserVolumes() {
		this.gameClient.setVolume(getMainVolume());
		this.gameClient.setSFXVolume(getSfxVolume());
		this.gameClient.setFreqVolume(getFrequentVolume());
		this.gameClient.setMusicVolume(getMusicVolume());
		this.gameClient.setDontShowTutorial(getDontShowTutorial());
	}

	async startMusic() {
		if (this.musicStarted) return;
		this.musicStarted = true;
		await this.musicLoading;
		if (!this.musicStarted) return; //stopped again while the assets were loading
		const sim = this.sounds[Sound.SimulationMusic];
		const shop = this.sounds[Sound.ShopMusic];
		if (sim && shop) {
			sim.volume = 0;
			shop.volume = 1;
			this.currentlyPlayingSimulation = false; //otherwise the next crossfade is skipped
			const simSource = sim.prepare(true);
			const shopSource = shop.prepare(true);
			if (simSource && shopSource) {

				this.applyUserVolumes();

				simSource.start();
				shopSource.start();
				this.musicSources = [simSource, shopSource];
			}
		}
	}

	stopMusic() {
		this.musicStarted = false;
		for (const source of this.musicSources) {
			try {
				source.stop();
			} catch (e) {
				//already ended
			}
			source.disconnect();
		}
		this.musicSources = [];
	}

	async switchBackgroundMusic(trackName: string) {
		await this.musicLoading;
		if (!this.musicStarted) {
			//e.g. reconnecting drops straight into shop/simulation without passing the lobby
			await this.startMusic();
		}
		this.crossfadeBackgroundMusic(trackName === 'simulation');
		// console.log(`Switching to ${trackName}`)
		// if (trackName === 'simulation') {
		// 	for (const [name, gains] of this.musicGains.entries()) {
		// 		console.log('Turning on track ' + name);
		// 		gains.gain.value = 1;
		// 	}
		// } else {
		// 	for (const [name, gains] of this.musicGains.entries()) {
		// 		if (name === trackName) {
		// 			console.log('Turning on track ' + name);
		// 			gains.gain.value = 1;
		// 		} else {
		// 			console.log('Turning off track ' + name);
		// 			gains.gain.value = 0;
		// 		}
		// 	}
		// }
	}

	crossfadeBackgroundMusic(isSimulation: boolean) {
		if (isSimulation === this.currentlyPlayingSimulation) return;
		this.currentlyPlayingSimulation = isSimulation
		const inSound = isSimulation ? this.sounds[Sound.SimulationMusic] : this.sounds[Sound.ShopMusic];
		const outSound = isSimulation ? this.sounds[Sound.ShopMusic] : this.sounds[Sound.SimulationMusic];

		if (!inSound || !outSound) return;

		//Value curves may not overlap (that throws), and one that has started can't be cancelled, so a switch during a
		//crossfade waits for it to finish. Each fade runs all the way, so the next one starts where it left off.
		const start = Math.max(this.audioContext.currentTime, this.crossfadeEndTime);
		inSound.gain.gain.setValueCurveAtTime(this.fadeCurveIn, start, CROSSFADE_DURATION);
		outSound.gain.gain.setValueCurveAtTime(this.fadeCurveOut, start, CROSSFADE_DURATION);
		this.crossfadeEndTime = start + CROSSFADE_DURATION;
	}

	toggleMute() {
		this._lastVolume = this.masterGain.gain.value;
		this._isMuted = !this._isMuted;
		this.masterGain.gain.value = this._isMuted ? 0 : this._lastVolume;
	}

	setVolume(value: number) {
		if (this._lastVolume === 0) {
			this._isMuted = true;
		} else {
			this._isMuted = false;
			this._lastVolume = value;
			this.masterGain.gain.value = value;
		}
	}

	setSFXVolume(value: number) {
		this.sfxGain.gain.value = value;
	}

	setFreqVolume(value: number) {
		this.frequentUISoundsGain.gain.value = value;
	}

	setMusicVolume(value: number) {
		this.musicGain.gain.value = value;
	}

	checkIfChrome(): boolean {
		// please note, 
		// that IE11 now returns undefined again for window.chrome
		// and new Opera 30 outputs true for window.chrome
		// but needs to check if window.opr is not undefined
		// and new IE Edge outputs to true now for window.chrome
		// and if not iOS Chrome check
		// so use the below updated condition
		// @ts-ignore
		var isChromium = window.chrome;
		var winNav = window.navigator;
		var vendorName = winNav.vendor;
		// @ts-ignore
		var isOpera = typeof window.opr !== "undefined";
		var isIEedge = winNav.userAgent.indexOf("Edg") > -1;
		var isIOSChrome = winNav.userAgent.match("CriOS");

		if (isIOSChrome) {
			// is Google Chrome on IOS
			return true;
		} else if (
			isChromium !== null &&
			typeof isChromium !== "undefined" &&
			vendorName === "Google Inc." &&
			isOpera === false &&
			isIEedge === false
		) {
			// is Google Chrome
			return true;
		} else {
			// not Google Chrome 
			return false;
		}
	}

	private loadSoundEffects() {

		this.addSound(Sound.Collision, [
			"collision/bfxrhit1.mp3",
			"collision/bfxrhit2.mp3",
			"collision/bfxrhit3.mp3"
		]);

		this.addSound(Sound.CollisionSmall, [
			"collision/AI_cl_impactbasic_01.ogg",
			"collision/AI_cl_impactbasic_02.ogg",
			"collision/AI_cl_impactbasic_03.ogg",
			"collision/AI_cl_impactbasic_04.ogg",
			"collision/AI_cl_impactbasic_05.ogg",
			"collision/AI_cl_impactbasic_06.ogg"
		]);

		this.addSound(Sound.CollisionHeavy, [
			"collision/AI_cl_impacttank_01.ogg",
			"collision/AI_cl_impacttank_02.ogg",
			"collision/AI_cl_impacttank_03.ogg",
			"collision/AI_cl_impacttank_04.ogg"
		]);

		this.addSound(Sound.Explosion, [
			"explosion/AI_ds_explosion_1b.ogg",
			"explosion/AI_ds_explosion_2b.ogg",
			"explosion/AI_ds_explosion_3b.ogg",
			"explosion/AI_ds_explosion_4b.ogg",
			"explosion/AI_ds_explosion_5b.ogg",
		]);

		this.addSound(Sound.Heal, [
			"heal/AI_bf_healbuffed_01.ogg",
			"heal/AI_bf_healbuffed_02.ogg",
			"heal/AI_bf_healbuffed_03.ogg",
			"heal/AI_bf_healbuffed_04.ogg",
			"heal/AI_bf_healbuffed_05.ogg",
			"heal/AI_bf_healbuffed_06.ogg",
		]);

		this.addSound(Sound.Healwave, [
			"healwave/AI_bf_healwave_01.ogg",
			"healwave/AI_bf_healwave_02.ogg",
			"healwave/AI_bf_healwave_03.ogg",
			"healwave/AI_bf_healwave_04.ogg",
		]);

		this.addSound(Sound.Shoot, [
			"shoot/AI_wp_shot_01b.ogg",
			"shoot/AI_wp_shot_02b.ogg",
			"shoot/AI_wp_shot_03b.ogg",
			"shoot/AI_wp_shot_04b.ogg",
			"shoot/AI_wp_shot_05b.ogg",
			"shoot/AI_wp_shot_06b.ogg",
			"shoot/AI_wp_shot_07b.ogg",
			"shoot/AI_wp_shot_08b.ogg",
			"shoot/AI_wp_shot_09b.ogg",
			"shoot/AI_wp_shot_10b.ogg",
			"shoot/AI_wp_shot_11b.ogg",
			"shoot/AI_wp_shot_12b.ogg",
		]);

		this.addSound(Sound.Death, [
			"death/AI_cell_death_01.ogg",
			"death/AI_cell_death_02.ogg",
			"death/AI_cell_death_03.ogg",
			"death/AI_cell_death_04.ogg",
			"death/AI_cell_death_05.ogg",
			"death/AI_cell_death_06.ogg",
		]);

		this.addSound(Sound.BloodCellDamage, [
			"blooddeath/AI_bloodcell_death_01.ogg",
			"blooddeath/AI_bloodcell_death_02.ogg",
			"blooddeath/AI_bloodcell_death_03.ogg",
			"blooddeath/AI_bloodcell_death_04.ogg",
		]);

		this.addSound(Sound.ChainConnect, [
			"chain/AI_cell_chainconnect_01.ogg",
			"chain/AI_cell_chainconnect_02.ogg",
			"chain/AI_cell_chainconnect_03.ogg",
			"chain/AI_cell_chainconnect_04.ogg",
			"chain/AI_cell_chainconnect_05.ogg",
			"chain/AI_cell_chainconnect_06.ogg",
		], { frequent: true });

		this.addSound(Sound.ChainDisconnect, [
			"chain/AI_cell_chaindisconnect_01a.ogg",
			"chain/AI_cell_chaindisconnect_02a.ogg",
			"chain/AI_cell_chaindisconnect_03a.ogg",
			"chain/AI_cell_chaindisconnect_04a.ogg",
			"chain/AI_cell_chaindisconnect_05a.ogg",
			"chain/AI_cell_chaindisconnect_06a.ogg",
		], { frequent: true });

		this.addSound(Sound.Combine, [
			"combine/AI_cell_combine_01.ogg",
			"combine/AI_cell_combine_02.ogg",
			"combine/AI_cell_combine_03.ogg",
			"combine/AI_cell_combine_04.ogg",
		]);

		this.addSound(Sound.WaterPlop, [
			"place/AI_cell_place_1.ogg",
			"place/AI_cell_place_2.ogg",
			"place/AI_cell_place_3.ogg",
			"place/AI_cell_place_4.ogg",
		],
			{ frequent: true }
		);

		this.addSound(Sound.PlaceCell, [
			"place/AI_cell_place_1.ogg",
			"place/AI_cell_place_2.ogg",
			"place/AI_cell_place_3.ogg",
			"place/AI_cell_place_4.ogg",
		]);

		this.addSound(Sound.Electricity, [
			"electricity/AI_ChainLightning_zap_01.ogg",
			"electricity/AI_ChainLightning_zap_02.ogg",
			"electricity/AI_ChainLightning_zap_03.ogg",
			"electricity/AI_ChainLightning_zap_04.ogg",
			"electricity/AI_ChainLightning_zap_05.ogg",
			"electricity/AI_ChainLightning_zap_06.ogg",
		]);

		this.addSound(Sound.ElectricalPop, [
			"electricity/AI_ChainLightning_pop_01.ogg",
			"electricity/AI_ChainLightning_pop_02.ogg",
			"electricity/AI_ChainLightning_pop_03.ogg",
			"electricity/AI_ChainLightning_pop_04.ogg",
			"electricity/AI_ChainLightning_pop_05.ogg",
			"electricity/AI_ChainLightning_pop_06.ogg",
		]);

		this.addSound(Sound.SellChime, [
			"sell-chime/chime1.ogg",
			"sell-chime/chime2.ogg",
			"sell-chime/chime3.ogg",
			"sell-chime/chime4.ogg"
		]);

		this.addSound(Sound.GetResource, [
			"resource/AI_pu_resource_1.ogg",
			"resource/AI_pu_resource_2.ogg",
			"resource/AI_pu_resource_3.ogg",
			"resource/AI_pu_resource_4.ogg",
		]);

		this.addSound(Sound.ProducedResource, [
			"resource/AI_resource_produce_A_01.ogg",
			"resource/AI_resource_produce_A_02.ogg",
			"resource/AI_resource_produce_A_03.ogg",
			"resource/AI_resource_produce_A_04.ogg",
			"resource/AI_resource_produce_A_05.ogg",
			"resource/AI_resource_produce_A_06.ogg",
			"resource/AI_resource_produce_A_07.ogg",
		]);

		this.addSound(Sound.Promote, [
			"promote/powerup1.wav",
			"promote/powerup2.wav",
			"promote/powerup3.wav"
		]);

		this.addSound(Sound.Reward,
			["promote/AI_rewardcell_A.ogg"]
		);

		this.addSound(Sound.HoverCell,
			["ui/AI_UI_hover_cell_D.ogg"],
			{ frequent: true }
		);
	}

	private loadUIEffects() {
		this.addUISound(Sound.Menu, [
			"ui/AI_UI_hover_button_1.ogg",
		], { frequent: true });

		this.addUISound(Sound.ButtonClick, [
			"ui/AI_UI_click_3.ogg",
			"ui/AI_UI_click_4.ogg"
		]);

		this.addUISound(Sound.Heartbeat, [
			"ui/heartbeat.wav"
		]);
	}

	private async loadMusic() {
		const loading = [];
		loading.push(this.addMusic(Sound.ShopMusic,
			"music/shop_v1.4.ogg"));

		loading.push(this.addMusic(Sound.SimulationMusic,
			"music/battle_v1.6.ogg"));

		await Promise.all(loading);
		this.musicReady();
	}
}